/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import PropTypes from 'prop-types';
import { MAIN_ICON_COLORS } from 'assets/components';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import ModalBodyLayout from '../shared/ModalBodyLayout';
import FeatureItem from '../../components/FeatureItem';
import {
  CARD_CONNECT_FINANCIAL_BLOCK,
  CARD_CONNECT_FINANCIAL_UNBLOCK,
  CARD_MONAVATE_BLOCK,
  CARD_MONAVATE_UNBLOCK,
  USA_CARD_CARD_BLOCK,
  USA_CARD_CARD_UNBLOCK,
} from './mutations';
import { showAlert } from '../../utilities/notifications';
import {
  cardProviders,
  manageCardScreens,
  PrivacyPolicySettingsUrls,
  TermsSettingsUrls,
  CardState,
  mainCardTypes,
  getCardCapabilities,
  shouldHideSetPin,
  shouldHideStatementEntry,
} from './constants';
import { useSetting } from '../../utilities/sharedHooks/settings';
import { FLOWS, sendStats } from '../../services/mixpanel';
import { MXP_CARD_MANAGEMENT_FREEZE_CARD } from './mixpanel-events';
import useIsUsUser from '../../utilities/sharedHooks/usUser';
import { useUserPersonalInfo } from '../../utilities/sharedHooks/user';
import {
  MONAVATE_CARD_STATUS,
  CARD_MONAVATE_PIN_CHECK,
  CONNECT_FINANCIAL_CARD_STATUS,
} from './queries';
import { dynamicAssets } from '../../utilities/helpers';
import Icon from '../../components/Icon';
import { ICONS } from '../../components/Icon/constants';
import { cardSettingsScreens } from './Settings/constants';

const ManageCard = ({
  cardStatus,
  cardProvider,
  enableCryptodraft,
  openCryptodraftModal,
  openScreen,
}) => {
  const { t: translate } = useTranslation();
  const history = useHistory();

  const isUsUser = useIsUsUser();
  const isReplacementRequested = cardStatus?.is_replacement_requested;

  const { selectedCountry } = useUserPersonalInfo();

  const selectedCard = useMemo(() => {
    switch (cardProvider) {
      case cardProviders.MONAVATE:
        return {
          BLOCK: CARD_MONAVATE_BLOCK,
          UNBLOCK: CARD_MONAVATE_UNBLOCK,
          STATUS: MONAVATE_CARD_STATUS,
          PRIVACY: PrivacyPolicySettingsUrls.CARD_MONAVATE_PRIVACY_POLICY_CONTENT_URL,
          TERMS: TermsSettingsUrls.CARD_MONAVATE_TERMS_CONTENT_URL,
        };

      case cardProviders.CONNECT_FINANCIAL:
        return {
          BLOCK: CARD_CONNECT_FINANCIAL_BLOCK,
          UNBLOCK: CARD_CONNECT_FINANCIAL_UNBLOCK,
          STATUS: CONNECT_FINANCIAL_CARD_STATUS,
          PRIVACY: PrivacyPolicySettingsUrls.CARD_GALILEO_PRIVACY_POLICY_CONTENT_URL,
          TERMS:
            isUsUser === false
              ? TermsSettingsUrls.CARD_CONNECT_FINANCIAL_ROW_TERMS_CONTENT_URL
              : TermsSettingsUrls.CARD_TERMS_CONTENT_URL,
        };

      default: {
        return {
          BLOCK: USA_CARD_CARD_BLOCK,
          UNBLOCK: USA_CARD_CARD_UNBLOCK,
          STATUS: '',
          PRIVACY: PrivacyPolicySettingsUrls.CARD_GALILEO_PRIVACY_POLICY_CONTENT_URL,
          TERMS: TermsSettingsUrls.CARD_GALILEO_TERMS_CONTENT_URL,
        };
      }
    }
  }, [cardProvider, isUsUser]);

  const isCardFrozen = useMemo(
    () => cardStatus?.cardState?.toUpperCase() === CardState.BLOCKED,

    [cardStatus?.cardState]
  );

  const setCardFrozen = (isFrozen) => {
    if (!cardStatus) {
      return;
    }
    if (isFrozen) {
      cardStatus.cardState = CardState.BLOCKED;
    } else {
      cardStatus.cardState = CardState.ACTIVE;
    }
  };

  const { mutate: blockCard } = useMutation(selectedCard.BLOCK, {
    onSettled(data, error) {
      sendStats(MXP_CARD_MANAGEMENT_FREEZE_CARD, { error, data }, FLOWS.CARD_MANAGEMENT);
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
        setCardFrozen(false);
      } else {
        showAlert({
          message: translate('card_has_been_frozen_successfully_message_text'),
          type: 'success',
        });

        setCardFrozen(true);
      }
    },
  });

  const { mutate: unblockCard } = useMutation(selectedCard.UNBLOCK, {
    onSettled(data, error) {
      sendStats(MXP_CARD_MANAGEMENT_FREEZE_CARD, { error, data }, FLOWS.CARD_MANAGEMENT);
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
        setCardFrozen(true);
      } else {
        showAlert({
          message: translate('card_has_been_unfrozen_successfully_message_text'),
          type: 'success',
        });

        setCardFrozen(false);
      }
    },
  });
  const handleChangeCardFreeze = (e) => {
    if (e.target.checked) {
      setCardFrozen(true);
      blockCard();
    } else {
      setCardFrozen(false);
      unblockCard();
    }
  };
  const [privacyPolicyContentUrl] = useSetting('content_urls', selectedCard.PRIVACY);
  const [termsContentUrl] = useSetting('content_urls', selectedCard.TERMS);

  const {
    data: pinExist,
    refetch: refetchCheckPIN,
    isLoading: isLoadingPINCheck,
  } = useQuery(CARD_MONAVATE_PIN_CHECK, {
    enabled: false,
  });

  useEffect(() => {
    if (cardProvider === cardProviders.MONAVATE) {
      refetchCheckPIN();
    }
  }, []);

  const alreadyReportedMonavateCardIssue =
    cardProvider === cardProviders.MONAVATE &&
    [CardState.STOLEN, CardState.LOST, CardState.DAMAGED].includes(
      cardStatus?.cardState?.toUpperCase()
    );

  const generalSettings = useMemo(
    () => [
      {
        label: translate('manage_card_setting_option_pin_setting_title'),
        subTitle: translate('manage_card_setting_option_pin_setting_subtitle'),
        image: dynamicAssets('./assets/images/eye.svg'),
        onClick: () => {
          openScreen(manageCardScreens.PIN_SETTINGS);
        },
        dataTestid: 'pin-setting-button',
        classes: '',
        loading: isLoadingPINCheck,
        disabled: isLoadingPINCheck || !pinExist?.success,
        hidden: cardStatus?.isVirtualCard && !getCardCapabilities(cardProvider).virtualCardPin,
      },
      {
        label: translate('manage_card_setting_option_set_pin_title'),
        subTitle: translate('manage_card_setting_option_set_pin_subtitle'),
        icon: <Icon icon={ICONS.REFRESH} color={MAIN_ICON_COLORS.WHITE} />,
        onClick: () => {
          history.push('/dashboard/card/settings', {
            initScreen: cardSettingsScreens.VERIFY_PASSWORD,
          });
        },
        dataTestid: 'setting-set-pin-button',
        classes: '',
        hidden: shouldHideSetPin(cardProvider, cardStatus),
      },
      {
        label: translate('manage_card_setting_option_cryptodraft_title'),
        subTitle: translate('manage_card_setting_option_cryptodraft_subtitle'),
        image: dynamicAssets('./assets/images/credit-limit.svg'),
        onClick: (e) => {
          openCryptodraftModal(e);
        },
        classes: !enableCryptodraft ? 'd-none' : '',
        hidden: false,
      },
      {
        label: translate('manage_card_replacement'),
        subTitle: translate('manage_card_replacement_subtitle'),
        image: dynamicAssets('./assets/images/credit-card.svg'),
        classes: '',
        onClick: () => {
          history.push('/dashboard/card/settings', {
            initScreen:
              cardStatus?.is_replacement_requested !== false
                ? cardSettingsScreens.REPORT_CARD_ISSUE
                : cardSettingsScreens.CONFIRM_REPLACEMENT_CARD_DETAILS,
          });
        },
        hidden:
          // hide card replacement flow DV-4815 until we test it properly
          // true ||
          isReplacementRequested !== false ||
          // Hide this button for French users DV-4733 and Virtual cards
          cardStatus?.isVirtualCard ||
          selectedCountry?.iso3166alpha2 === 'FR',
      },
      {
        label: translate('manage_card_setting_order_replacement_title'),
        subTitle: translate('manage_card_setting_order_replacement_subtitle'),
        image: dynamicAssets('./assets/images/credit-card.svg'),
        classes: '',
        onClick: () => {
          history.push('/dashboard/card/order-card', {
            isCardReplacement: true,
            mainCardType: mainCardTypes.PHYSICAL,
          });
        },
        hidden:
          // hide card replacement flow DV-4815 until we test it properly
          // true ||
          !alreadyReportedMonavateCardIssue ||
          // Hide this button for French users DV-4733 and Virtual cards
          cardStatus?.isVirtualCard ||
          selectedCountry?.iso3166alpha2 === 'FR',
      },
      {
        label: translate('manage_card_report_card_issue'),
        subTitle: translate('manage_card_report_card_issue_subtitle'),
        image: dynamicAssets('./assets/images/credit-card.svg'),
        classes: '',
        onClick: () => {
          history.push('/dashboard/card/settings', {
            initScreen:
              cardStatus?.is_replacement_requested !== false
                ? cardSettingsScreens.REPORT_CARD_ISSUE
                : cardSettingsScreens.CONFIRM_DETAILS,
          });
        },
        hidden:
          isReplacementRequested === false ||
          cardStatus?.cardState?.toUpperCase() !== CardState.ACTIVE ||
          cardStatus?.isVirtualCard,
      },
      {
        label: translate('manage_card_setting_option_transaction_states_title'),
        subTitle:
          cardProvider === cardProviders.MONAVATE
            ? translate('manage_card_setting_option_view_download_csv_subtitle')
            : translate('manage_card_setting_option_view_download_pdf_subtitle'),
        image: dynamicAssets('./assets/images/document.svg'),
        onClick: () => {
          openScreen(manageCardScreens.STATEMENT_OF_TRANSACTIONS);
        },
        dataTestid: 'statement-transactions-button',
        classes: '',
        hidden: shouldHideStatementEntry(cardProvider, isMobile),
      },
      {
        label: translate('manage_card_setting_option_terms_and_conditions_title'),
        subTitle: translate('manage_card_setting_option_view_download_pdf_subtitle'),
        image: dynamicAssets('./assets/images/document.svg'),
        link: true,
        linkDownloadTitle: translate('manage_card_setting_option_terms_and_conditions_title'),
        linkDownloadUrl: termsContentUrl,
        classes: '',
        hidden: false,
      },
      {
        label: translate('manage_card_setting_option_privacy_policy_title'),
        subTitle: translate('manage_card_setting_option_view_download_pdf_subtitle'),
        image: dynamicAssets('./assets/images/document.svg'),
        link: true,
        linkDownloadTitle: translate('manage_card_setting_option_privacy_policy_title'),
        linkDownloadUrl: privacyPolicyContentUrl,
        classes: '',
        hidden: false,
      },
    ],
    [
      privacyPolicyContentUrl,
      termsContentUrl,
      cardProvider,
      cardStatus,
      isReplacementRequested,
      alreadyReportedMonavateCardIssue,
      pinExist?.success,
      isLoadingPINCheck,
    ]
  );

  const usSettings = useMemo(
    () => [
      {
        label: translate('manage_card_setting_option_pin_setting_title'),
        subTitle: translate('manage_card_setting_option_pin_setting_subtitle'),
        image: dynamicAssets('./assets/images/eye.svg'),
        onClick: () => {
          openScreen(manageCardScreens.PIN_SETTINGS);
        },
        classes: '',
        hidden: !getCardCapabilities(cardProvider).cardPin,
      },
      ...generalSettings.filter(
        (settingOption) => settingOption?.dataTestid !== 'pin-setting-button'
      ),
      {
        label: translate('manage_card_setting_option_card_fees_title'),
        subTitle: translate('manage_card_setting_option_card_fees_subtitle'),
        image: dynamicAssets('./assets/images/document.svg'),
        onClick: () => {
          openScreen(manageCardScreens.CARD_FEES);
        },
        hidden: process.env.REACT_APP_MINIMIZED_US_VERSION === 'true',
      },
    ],
    [generalSettings]
  );

  const settings = useMemo(
    () => (isUsUser ? usSettings : generalSettings),
    [isUsUser, generalSettings, usSettings]
  );

  const renderHeader = () => (
    <div>
      <p>
        <b className="text-f5 text-dark me-auto">{translate('manage_card_title')}</b>
      </p>
      <div className="form-check form-switch d-flex p-0 align-items-center mb-4 mt-4">
        <div className="d-flex flex-column">
          <b className="text-f5 text-dark">{translate('manage_card_setting_freeze_card_title')}</b>
          <p className="text-f6 text-shadow">
            {translate('manage_card_setting_freeze_card_subtitle')}
          </p>
        </div>
        <input
          data-testid="form-switch-freeze-card"
          className="form-check-input d-flex ms-auto m-0"
          type="checkbox"
          onChange={handleChangeCardFreeze}
          checked={isCardFrozen}
          disabled={
            (getCardCapabilities(cardProvider).freezeGatedByStatus && !cardStatus?.isFreezable) ||
            (cardProvider === cardProviders.MONAVATE &&
              cardStatus?.cardState?.toLowerCase() === CardState.ACTIVATION)
          }
        />
      </div>
    </div>
  );

  return (
    <ModalBodyLayout className="h-auto" withBack={false} renderHeader={renderHeader}>
      <div className="mt-2">
        {settings
          .filter((setting) => !setting.hidden)
          .map((settingOption) => (
            <FeatureItem
              iconElement={settingOption.icon}
              key={settingOption.label}
              link={settingOption?.link}
              linkDownloadUrl={settingOption?.linkDownloadUrl}
              linkDownloadTitle={settingOption?.linkDownloadTitle}
              className={`mb-1 ${settingOption.classes}`}
              label={settingOption.label}
              subtitle={settingOption.subTitle}
              image={settingOption.image}
              dataTestid={settingOption?.dataTestid}
              onClick={settingOption.onClick}
              loading={settingOption.loading ?? false}
              disabled={settingOption.disabled}>
              <p>{settingOption.label}</p>
              <p className="text-f6">{settingOption.subTitle}</p>
            </FeatureItem>
          ))}
      </div>
    </ModalBodyLayout>
  );
};

export default ManageCard;

ManageCard.propTypes = {
  cardStatus: PropTypes.object.isRequired,
  cardProvider: PropTypes.string.isRequired,
  openScreen: PropTypes.func.isRequired,
  enableCryptodraft: PropTypes.bool.isRequired,
  openCryptodraftModal: PropTypes.func.isRequired,
};
